var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { TimespanUnit } from '@debbie/common/dist';
import { CaseStatus } from '@debbie/common/dist/cases';
import { caseFilterhash } from '@debbie/common/dist/insights';
import { PathType, TimingType, } from '@debbie/common/dist/workflows';
import { c, h } from '@debbie/cortex/dist';
import * as insightsAPI from '@debbie/cortex/dist/api/insights';
import { selectExclusiveContent } from '@debbie/cortex/dist/exclusiveContent/selectors';
import { selectMe } from '@debbie/cortex/dist/me/selectors';
import { selectUpdateTypesMap } from '@debbie/cortex/dist/update-types/selectors';
import { Panel, Button, Well, Input, CopyToClipboard, Checkbox, Table, Loading, Modal, PlaceholderLoader, } from '..';
import { ExternalLink, X } from '../Icons';
import Frown from '../Icons/Frown';
import Meh from '../Icons/Meh';
import Smile from '../Icons/Smile';
import RequirementsInput from './RequirementsInput';
import TimingInput from './TimingInput';
import { useEndReasons } from '@debbie/cortex/dist/end-reasons';
import { objectBy } from '@debbie/common/dist/util';
import UpdateTypeSettings from '../UpdateTypeSettings';
import { useWorkflowStats, useWorkflowTaskStats, } from '@debbie/cortex/dist/workflows';
const NodeEditor = (props) => {
    const updateTypesMap = useSelector(selectUpdateTypesMap);
    const lang = useSelector((s) => s.lang);
    const exclusiveContent = useSelector(selectExclusiveContent);
    const me = useSelector(selectMe);
    const statsLoadable = useWorkflowStats(props.workflowId, {});
    const stats = h.hasData(statsLoadable) ? statsLoadable.data : null;
    const endReasons = useEndReasons();
    const endReasonLookup = h.hasData(endReasons)
        ? objectBy(endReasons.data, (er) => er.id)
        : {};
    const [redirecting, setRedirecting] = useState({});
    const [editModalOpen, setEditModalOpen] = useState(false);
    const { node } = props;
    const updateType = updateTypesMap[node.data.updateTypeId];
    const taskStats = stats && stats.tasks.find((taskStats) => taskStats.taskId === node.id);
    const onChangeAlternativePaths = (index, change) => {
        props.onChange(node.id, {
            alternativePaths: node.data.alternativePaths.map((path, i) => {
                if (i === index) {
                    return Object.assign(Object.assign({}, path), change);
                }
                else {
                    return path;
                }
            }),
        });
    };
    const goToCases = (caseFilter) => __awaiter(void 0, void 0, void 0, function* () {
        if (!redirecting[caseFilterhash(caseFilter)]) {
            setRedirecting(Object.assign(Object.assign({}, redirecting), { [caseFilterhash(caseFilter)]: true }));
            const caseQueue = yield insightsAPI.getCaseFilterQueue(caseFilter);
            setRedirecting(Object.assign(Object.assign({}, redirecting), { [caseFilterhash(caseFilter)]: false }));
            c.redirect.redirect(`/cases/${caseQueue.join(',')}`);
        }
    });
    const currentCasesCaseFilter = {
        caseStatuses: [CaseStatus.VERIFIED],
        currentTaskIds: [node.id],
    };
    return (React.createElement(NodeEditorPanel, null,
        editModalOpen && updateType && (React.createElement(Modal, { header: " ", onToggle: () => setEditModalOpen(!editModalOpen) },
            React.createElement(UpdateTypeSettings, { updateTypeId: updateType.updateTypeId, preventDelete: true }))),
        React.createElement("h3", { style: { marginTop: 0 } }, me && (exclusiveContent === null || exclusiveContent === void 0 ? void 0 : exclusiveContent.includes(me.id)) ? (React.createElement(CopyToClipboard, { text: node.id }, lang.workflows.task)) : (lang.workflows.task)),
        props.editable && (React.createElement(Button, { block: true, size: "mini", theme: "secondary", onClick: () => setEditModalOpen(!editModalOpen) }, lang.updateTypesSettings.editUpdateType)),
        React.createElement("br", null),
        updateType && !(updateType.procedure && updateType.procedureEnabled) && (React.createElement(Well, null,
            React.createElement("h3", null, lang.workflows.pause),
            React.createElement(Checkbox, { placeholder: lang.workflows.pauseUnlimited, checked: !node.data.pauseAtTiming, onChange: (e) => {
                    if (e.target.checked) {
                        props.onChange(node.id, {
                            pauseAtTiming: null,
                        });
                    }
                    else {
                        props.onChange(node.id, {
                            pauseAtTiming: {
                                type: TimingType.Timespan,
                                details: {
                                    timespan: [0, TimespanUnit.Minutes],
                                },
                            },
                        });
                    }
                } }),
            node.data.pauseAtTiming && (React.createElement(TimingInput, { timing: node.data.pauseAtTiming, onChange: (timing) => props.onChange(node.id, {
                    pauseAtTiming: timing,
                }) })))),
        React.createElement("h3", null, lang.workflows.stats),
        React.createElement(TaskStats, { taskId: node.id, workflowId: props.workflowId }),
        taskStats && (React.createElement("div", null,
            React.createElement("h4", null, lang.workflows.endedCases),
            React.createElement(Table, { zebra: true, sumRow: true, style: { fontSize: '0.7em' } },
                taskStats.endedCases
                    .sort((a, b) => b.count - a.count)
                    .map((reason) => {
                    var _a;
                    const caseFilter = {
                        caseStatuses: [CaseStatus.ENDED],
                        caseEndReasonIds: [reason.endReasonId],
                        currentTaskIds: [node.id],
                    };
                    const endReason = endReasonLookup[reason.endReasonId];
                    return (React.createElement("tr", null,
                        React.createElement("td", null, (_a = lang.endReason[endReason.handle]) !== null && _a !== void 0 ? _a : endReason.label),
                        React.createElement("td", { style: { textAlign: 'right' } }, reason.count),
                        React.createElement("td", { style: { width: '30px' } }, redirecting[caseFilterhash(caseFilter)] ? (React.createElement(Loading, { style: {
                                width: '15px',
                                margin: '0px 1px -4px 1px',
                            } })) : (React.createElement(ExternalLink, { style: { cursor: 'pointer' }, onClick: () => __awaiter(void 0, void 0, void 0, function* () {
                                goToCases(caseFilter);
                            }) })))));
                }),
                React.createElement("tr", null,
                    React.createElement("td", null, lang.workflows.sum),
                    React.createElement("td", { style: { textAlign: 'right' } }, taskStats.endedCases.reduce((acc, reason) => reason.count + acc, 0)),
                    React.createElement("td", { style: { width: '30px' } }))),
            React.createElement("h4", null, lang.workflows.totalCases),
            React.createElement(Table, { zebra: true, style: { fontSize: '0.7em' } },
                React.createElement("tr", null,
                    React.createElement("td", null, lang.workflows.sum),
                    React.createElement("td", { style: { textAlign: 'right' } }, taskStats.totalCases),
                    React.createElement("td", null))),
            React.createElement("h4", null, lang.workflows.currentCases),
            React.createElement(Table, { zebra: true, style: { fontSize: '0.7em' } },
                React.createElement("tr", null,
                    React.createElement("td", null, lang.workflows.sum),
                    React.createElement("td", { style: { textAlign: 'right' } }, taskStats.currentTaskCases),
                    React.createElement("td", { style: { width: '30px' } }, !!taskStats.currentTaskCases &&
                        (redirecting[caseFilterhash(currentCasesCaseFilter)] ? (React.createElement(Loading, { style: {
                                width: '15px',
                                margin: '0px 1px -4px 1px',
                            } })) : (React.createElement(ExternalLink, { style: { cursor: 'pointer' }, onClick: () => __awaiter(void 0, void 0, void 0, function* () {
                                goToCases(currentCasesCaseFilter);
                            }) })))))))),
        (props.editable || !!node.data.alternativePaths.length) && (React.createElement("h3", null, lang.workflows.alternativePaths)),
        node.data.alternativePaths.map((path, index) => (React.createElement(Well, { key: path.id, style: { marginBottom: '10px' } },
            React.createElement("h4", null,
                lang.workflows.alternativePath,
                ' ',
                props.editable && (React.createElement(X, { style: { float: 'right', cursor: 'pointer' }, onClick: () => props.onChange(node.id, {
                        alternativePaths: node.data.alternativePaths.filter((_, i) => i !== index),
                    }) }))),
            React.createElement("h4", null, lang.workflows.source),
            React.createElement(Input, { placeholder: lang.workflows.source, value: path.source, onChange: (e) => onChangeAlternativePaths(index, {
                    source: e.target.value,
                }) }),
            React.createElement("h4", null, lang.workflows.priority),
            React.createElement(Input, { placeholder: lang.workflows.priority, type: "number", value: path.priority, onChange: (e) => {
                    const val = e.target.value;
                    const isNumber = !isNaN(parseInt(val));
                    if (isNumber) {
                        onChangeAlternativePaths(index, {
                            priority: parseInt(val),
                        });
                    }
                } }),
            React.createElement("h4", null, lang.workflows.timing),
            React.createElement(TimingInput, { timing: path.timing, onChange: (timing) => onChangeAlternativePaths(index, {
                    timing,
                }) }),
            React.createElement("h4", null,
                lang.workflows.requirements,
                " "),
            React.createElement(RequirementsInput, { requirements: path.requirements, onChange: (requirements) => onChangeAlternativePaths(index, {
                    requirements,
                }) })))),
        props.editable && (React.createElement(Button, { theme: "secondary", block: true, size: "small", onClick: () => props.onChange(node.id, {
                alternativePaths: node.data.alternativePaths.concat({
                    type: PathType.ForMigration,
                    id: crypto.randomUUID(),
                    manual: false,
                    priority: 1,
                    source: 'from id',
                    target: node.id,
                    requirements: [],
                    timing: {
                        type: TimingType.Timespan,
                        details: {
                            timespan: [12, TimespanUnit.Days],
                        },
                    },
                }),
            }) }, lang.workflows.addAlternativePath))));
};
export default NodeEditor;
const NodeEditorPanel = styled(Panel) `
  position: absolute;
  width: 300px;
  z-index: 9;
  left: 40px;
  top: 80px;

  .body {
    max-height: calc(100vh - 240px);
    overflow-y: scroll;
  }
`;
const TaskStats = (props) => {
    var _a, _b, _c;
    const lang = useSelector((s) => s.lang);
    const workflowTaskStatsLoadable = useWorkflowTaskStats(props.workflowId, props.taskId, {});
    const workflowTaskStats = h.hasData(workflowTaskStatsLoadable)
        ? workflowTaskStatsLoadable.data
        : null;
    return workflowTaskStats && !h.isLoading(workflowTaskStatsLoadable) ? (React.createElement("div", null,
        !!Object.entries(workflowTaskStats.incomingInteractions).length && (React.createElement(React.Fragment, null,
            React.createElement("h4", null, lang.workflows.incomingInteractionsStats),
            React.createElement(Table, { zebra: true, style: { fontSize: '0.7em' } }, Object.entries(workflowTaskStats.incomingInteractions).map(([interactionType, count]) => (React.createElement("tr", null,
                React.createElement("td", null, lang.interactionTypes[interactionType]),
                React.createElement("td", { style: { textAlign: 'right' } }, count))))))),
        React.createElement("h4", null, lang.workflows.paymentPlansCreatedStats),
        React.createElement(Table, { zebra: true, style: { fontSize: '0.7em' } },
            React.createElement("tr", null,
                React.createElement("td", null, workflowTaskStats.paymentPlansCreated || 0))),
        !!workflowTaskStats.satisfactions.reduce((acc, s) => acc + s.count, 0) && (React.createElement(React.Fragment, null,
            React.createElement("h4", null, lang.workflows.satisfactionStats),
            React.createElement("div", { style: { textAlign: 'center' } },
                React.createElement(SatisfactionStat, { count: (_a = workflowTaskStats.satisfactions.find((s) => s.satisfaction === 1)) === null || _a === void 0 ? void 0 : _a.count },
                    React.createElement(Frown, { style: {
                            width: '30px',
                            height: '30px',
                        } })),
                React.createElement(SatisfactionStat, { count: (_b = workflowTaskStats.satisfactions.find((s) => s.satisfaction === 3)) === null || _b === void 0 ? void 0 : _b.count },
                    React.createElement(Meh, { style: {
                            width: '30px',
                            height: '30px',
                        } })),
                React.createElement(SatisfactionStat, { count: (_c = workflowTaskStats.satisfactions.find((s) => s.satisfaction === 5)) === null || _c === void 0 ? void 0 : _c.count },
                    React.createElement(Smile, { style: {
                            width: '30px',
                            height: '30px',
                        } }))))))) : (React.createElement(PlaceholderLoader, null));
};
const SatisfactionStat = (props) => {
    return (React.createElement(StyledSatisfaction, null,
        props.children,
        React.createElement("div", null, props.count || 0)));
};
const StyledSatisfaction = styled.div `
  display: inline-block;
  margin-right: 8px;
  text-align: center;
  font-size: 0.7em;
`;
//# sourceMappingURL=NodeEditor.js.map